import { Component, Input, OnInit } from '@angular/core';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import { CreateProformSubmissionPayload } from '../../../types/payloads/proforms.payloads';
import {
  ProFormsAnswerOptionAPIResponse,
  ProFormsQuestionAPIResponse,
  ProFormsSubmissionAPIResponse,
} from '../../../types/responses/proforms.responses';

interface CheckboxGridFieldTypeSpecs {
  rowNames: string[];
  colNames: string[];
}

@Component({
  selector: 'app-proforms-question-answer-checkbox-grid',
  standalone: false,
  templateUrl: './proforms-question-answer-checkbox-grid.component.html',
  styleUrl: './proforms-question-answer-checkbox-grid.component.scss',
})
export class ProformsQuestionAnswerCheckboxGridComponent implements OnInit {
  @Input() viewState: ProformViewStates = ProformViewStates.PREVIEW;

  @Input() showAnswers = 0;

  @Input() question: ProFormsQuestionAPIResponse;

  answerOptionIds: number[] = [];

  viewStates = ProformViewStates;

  hasCorrectAnswers = false;

  serializedFieldTypeSpecs: CheckboxGridFieldTypeSpecs = {
    rowNames: [],
    colNames: [],
  };

  constructor(private proformsSubmissionService: ProformsSubmissionService) {}

  ngOnInit(): void {
    if (this.question) {
      this.serializedFieldTypeSpecs = this.getFieldTypeSpecs();

      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            this.setAnswerOptionIdsFromSubmission(submission);
          }
        );
      } else if (this.viewState === this.viewStates.EDIT) {
        this.proformsSubmissionService.newSubmission.subscribe((submission) => {
          this.setAnswerOptionIdsFromSubmission(submission);
        });
      }

      this.hasCorrectAnswers = this.question.answerOptions.some(
        (option) => option.is_correct
      );
    }
  }

  get isInputInteractive(): boolean {
    return this.viewState === this.viewStates.EDIT;
  }

  setAnswerOptionIdsFromSubmission(
    submission:
      | CreateProformSubmissionPayload
      | ProFormsSubmissionAPIResponse
      | null
  ): void {
    if (!submission?.answers) {
      this.answerOptionIds = [];
      return;
    }

    this.answerOptionIds = submission.answers
      .filter((a) => a.proform_form_question_id === this.question?.id)
      .map((a) => a.proform_form_question_answer_option_id)
      .filter((id): id is number => id !== undefined);
  }

  onOptionChange(optionId: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const isChecked = inputElement.checked;

    if (isChecked) {
      this.proformsSubmissionService.addAnswerToPayload(
        {
          proform_form_question_id: this.question.id,
          proform_form_question_answer_option_id: optionId,
        },
        this.question.field_type
      );
    } else {
      this.proformsSubmissionService.removeAnswerFromPayload(optionId);
    }
  }

  onCellChange(row: string, col: string, event: Event): void {
    const option = this.getAnswerOptionForCell(row, col);
    if (!option) {
      return;
    }

    this.onOptionChange(option.id, event);
  }

  isCellChecked(row: string, col: string): boolean {
    const option = this.getAnswerOptionForCell(row, col);
    return !!option && this.answerOptionIds.includes(option.id);
  }

  showIncorrectForCell(row: string, col: string): boolean {
    const option = this.getAnswerOptionForCell(row, col);
    if (!option) {
      return false;
    }

    const isChecked = this.isCellChecked(row, col);
    const isCorrect = !!option.is_correct;

    // Show incorrect marker for either a wrong selection or a missed correct cell.
    return (isChecked && !isCorrect) || (!isChecked && isCorrect);
  }

  showCorrectForCell(row: string, col: string): boolean {
    const option = this.getAnswerOptionForCell(row, col);
    return !!option && this.isCellChecked(row, col) && !!option.is_correct;
  }

  private getFieldTypeSpecs(): CheckboxGridFieldTypeSpecs {
    if (!this.question?.field_type_specs) {
      return { rowNames: [], colNames: [] };
    }

    try {
      const parsed = JSON.parse(this.question.field_type_specs);

      return {
        rowNames: Array.isArray(parsed?.rowNames) ? parsed.rowNames : [],
        colNames: Array.isArray(parsed?.colNames) ? parsed.colNames : [],
      };
    } catch {
      return { rowNames: [], colNames: [] };
    }
  }

  private getAnswerOptionForCell(
    row: string,
    col: string
  ): ProFormsAnswerOptionAPIResponse | undefined {
    if (!this.question?.answerOptions?.length) {
      return undefined;
    }

    const byEncodedAnswer = this.question.answerOptions.find((option) =>
      [
        `${row}|||${col}`,
        `${row}::${col}`,
        `${row}|${col}`,
        `${row},${col}`,
        `${row} - ${col}`,
      ].includes(option.answer)
    );

    if (byEncodedAnswer) {
      return byEncodedAnswer;
    }

    const rowIndex = this.serializedFieldTypeSpecs.rowNames.indexOf(row);
    const colIndex = this.serializedFieldTypeSpecs.colNames.indexOf(col);

    if (rowIndex < 0 || colIndex < 0) {
      return undefined;
    }

    const colCount = this.serializedFieldTypeSpecs.colNames.length;
    const answerOptionIndex = rowIndex * colCount + colIndex;

    return this.question.answerOptions[answerOptionIndex];
  }
}
